import type { StepView } from '../../lib/api'

import { ChoiceDock } from './ChoiceDock'
import { FaqDock } from './FaqDock'
import type { Exchange, FieldWalk } from './FieldWalk'
import { FormDock } from './FormDock'
import { PaymentDock } from './PaymentDock'
import { ReviewDock } from './ReviewDock'
import { TerminalDock } from './TerminalDock'

/**
 * Post an answer.
 *
 * `echo` is what the transcript prints as the customer's side of the exchange:
 * a single label for a step answered in one tap, or the list of questions and
 * answers for a form that was asked one field at a time.
 */
export type Echo = string | Exchange[]
export type Send = (payload: Record<string, unknown>, echo?: Echo) => void

export interface DockProps {
  view: StepView
  busy: boolean
  /** Where a form step is up to. Empty on every other kind of step. */
  walk: FieldWalk
  onSend: Send
  /** Swap the whole view — the proof endpoint answers with a fresh StepView. */
  onReplace: (view: StepView) => void
  /** A transient message. The step stays; the customer can try again. */
  onNotice: (message: string) => void
}

/**
 * Whatever the current step needs the customer to DO.
 *
 * A switch on `step.type` and nothing else. It is not a router: every branch
 * here draws the step it was given, and none of them chooses which step comes
 * next.
 */
export function StepDock(props: DockProps) {
  switch (props.view.step.type) {
    case 'choice':
      return <ChoiceDock {...props} />

    case 'form':
      return <FormDock {...props} />

    case 'review':
      return <ReviewDock {...props} />

    case 'faqLookup':
      return <FaqDock {...props} />

    case 'payment':
      return <PaymentDock {...props} />

    case 'terminal':
      return <TerminalDock {...props} />

    case 'action':
      /*
       * Unreachable in practice — the runtime settles action steps before it
       * returns, so a renderer never sees one. Rendered as nothing rather than
       * thrown, so a mistake in the flow definition degrades to a dead dock
       * instead of a blank screen with a stack trace behind it.
       */
      return null
  }
}
